import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen,
  Briefcase,
  WalletCards,
  GraduationCap,
  Settings,
  Sparkles,
  Eye,
  ShieldCheck,
  ShoppingBag,
  UserCheck,
  Users,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const DecisionLink = ({ title, description, icon: Icon, to, count }) => (
  <Link
    to={to}
    className="kd9-card p-5 flex gap-3 items-center group"
    data-testid={`director-link-${to.replaceAll("/", "-")}`}
  >
    <Icon size={22} style={{ color: "var(--kd9-gold)" }} />
    <div className="flex-1 min-w-0">
      <div className="font-cinzel">{title}</div>
      <div
        className="font-cormorant"
        style={{ color: "var(--kd9-text-soft)" }}
      >
        {description}
      </div>
    </div>
    {count !== undefined && count !== null && (
      <span
        className="min-w-7 h-7 px-2 rounded-full flex items-center justify-center font-mont text-xs"
        style={{
          color: "var(--kd9-gold)",
          border: "1px solid var(--kd9-border)",
          background: "var(--kd9-bg-2)",
        }}
      >
        {count}
      </span>
    )}
    <ArrowRight size={14} aria-hidden="true" />
  </Link>
);

const DirectorDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      api.get("/api/director/system/summary"),
      api.get("/api/director/audit", { params: { limit: 6 } }),
    ])
      .then(([summaryResponse, auditResponse]) => {
        if (!active) return;
        setSummary(summaryResponse.data);
        setAudit(auditResponse.data?.items || []);
      })
      .catch((requestError) => {
        if (active) setError(formatApiError(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [version]);

  const cards = useMemo(
    () =>
      summary
        ? [
            [summary.users, "Usuarios activos", Users],
            [summary.diagnostics, "Diagnósticos activos", Sparkles],
            [summary.courses, "Cursos activos", BookOpen],
            [summary.products, "Productos activos", ShoppingBag],
            [summary.landing_diagnostics + summary.landing_courses, "Publicaciones en landing", Eye],
            [`${new Intl.NumberFormat("es-CO").format(Number(summary.marketplace_energy) || 0)} KD`, "Energía Marketplace", WalletCards],
          ]
        : [],
    [summary]
  );

  const decisions = [
    {
      title: "Entregas finales",
      description: "Autorización final después de la revisión administrativa",
      icon: ShieldCheck,
      to: "/app/entregas-finales",
    },
    {
      title: "Academia Premium",
      description: "Conceder o revocar acceso Premium académico",
      icon: GraduationCap,
      to: "/app/premium",
    },
    {
      title: "Solicitudes de roles",
      description: "Decidir habilitaciones internas",
      icon: UserCheck,
      to: "/app/solicitudes-roles",
    },
  ];

  const editorial = [
    { title: "Diagnósticos", description: "Catálogo, requisitos, precios y publicación", icon: Sparkles, to: "/app/super-diagnosticos", count: summary?.landing_diagnostics },
    { title: "Academia", description: "Cursos, módulos, videos y evaluaciones", icon: GraduationCap, to: "/app/super-cursos", count: summary?.landing_courses },
    { title: "Marketplace KD9", description: "Moderación, catálogo, publicación y gobierno de productos", icon: ShoppingBag, to: "/app/super-marketplace", count: summary?.pending_products },
    { title: "Control del landing", description: "Secciones, anuncios, idiomas, imágenes y orden", icon: Eye, to: "/app/control-landing" },
  ];

  const governance = [
    {
      title: "Usuarios",
      description: "Estados y gobierno de cuentas",
      icon: Users,
      to: "/app/usuarios",
    },
    {
      title: "Actividad y sesiones",
      description: "Uso real, último acceso y tiempo activo de la plataforma",
      icon: Activity,
      to: "/app/actividad-usuarios",
    },
    {
      title: "Radar de Retención KD9",
      description: "Ciclo de vida, recurrencia y señales tempranas de abandono",
      icon: BarChart3,
      to: "/app/radar-retencion",
    },
    {
      title: "Roles y equipo de confianza",
      description: "Crear cuentas de autoridad y asignar cargos reales",
      icon: ShieldCheck,
      to: "/app/super-roles",
    },
    {
      title: "Gobierno de anfitriones",
      description: "Abre Gobierno de Consultores para activar Templo Vivo y administrar capacidades individuales",
      icon: Briefcase,
      to: "/app/consultores",
    },
    {
      title: "Configuración global",
      description: "Políticas y parámetros estratégicos",
      icon: Settings,
      to: "/app/configuracion-global",
    },
  ];

  const control = [
    {
      title: "Analítica global",
      description: "Indicadores reales del ecosistema",
      icon: BarChart3,
      to: "/app/analitica-global",
    },
    {
      title: "Auditoría",
      description: "Trazabilidad de acciones críticas",
      icon: ShieldCheck,
      to: "/app/auditoria",
    },
    {
      title: "Incidentes escalados",
      description: "Casos que requieren autoridad de Dirección",
      icon: AlertTriangle,
      to: "/app/tickets-escalados",
    },
  ];

  const pendingTotal = Number(summary?.pending_products || 0);

  return (
    <PageShell
      kicker="Director KD9 · Núcleo"
      title="DASHBOARD DIRECTOR"
      desc="Gobierno estratégico, decisiones finales y trazabilidad real del ecosistema KD9."
      testId="director-dashboard"
    >
      {error && (
        <div className="kd9-card p-4 mb-5 flex flex-wrap items-center gap-3" style={{ color: "#FCA5A5" }} role="alert">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setVersion((value) => value + 1)} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] uppercase tracking-widest">Reintentar</button>
        </div>
      )}

      {loading && (
        <div className="kd9-card p-6 mb-6 font-mont text-sm" aria-live="polite" data-testid="director-dashboard-loading">
          Cargando indicadores y decisiones del Director…
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {cards.map(([value, label, Icon]) => (
          <div key={label} className="kd9-card p-5">
            <Icon size={20} style={{ color: "var(--kd9-gold)" }} />
            <div className="font-cinzel kd9-gold-text text-3xl mt-3">
              {value}
            </div>
            <div className="font-mont text-[10px] uppercase tracking-widest">
              {label}
            </div>
          </div>
        ))}
      </div>

      {summary && pendingTotal > 0 && (
        <div
          className="kd9-card p-5 mb-8 flex items-center gap-4"
          style={{ borderColor: "var(--kd9-gold)" }}
        >
          <AlertTriangle size={24} style={{ color: "var(--kd9-gold)" }} />
          <div className="flex-1">
            <div className="font-cinzel">Decisiones pendientes</div>
            <div
              className="font-cormorant"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              Hay {pendingTotal} productos del Marketplace que requieren una decisión de Dirección.
            </div>
          </div>
        </div>
      )}

      <h2 className="font-cinzel kd9-gold-text text-xl mb-4">
        CENTRO DE DECISIONES
      </h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {decisions.map((item) => (
          <DecisionLink key={item.title} {...item} />
        ))}
      </div>

      <h2 className="font-cinzel kd9-gold-text text-xl mb-4">CENTROS PRINCIPALES KD9</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">{editorial.map((item)=><DecisionLink key={item.title} {...item}/>)}</div>

      <h2 className="font-cinzel kd9-gold-text text-xl mb-4">
        GOBIERNO DEL ECOSISTEMA
      </h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {governance.map((item) => (
          <DecisionLink key={item.title} {...item} />
        ))}
      </div>

      <h2 className="font-cinzel kd9-gold-text text-xl mb-4">
        CONTROL Y TRAZABILIDAD
      </h2>
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {control.map((item) => (
          <DecisionLink key={item.title} {...item} />
        ))}
      </div>

      <section className="kd9-card overflow-hidden">
        <header
          className="p-5 border-b font-cinzel kd9-gold-text"
          style={{ borderColor: "var(--kd9-border)" }}
        >
          AUDITORÍA RECIENTE · {summary?.audit_events || 0} EVENTOS
        </header>
        {audit.length === 0 && (
          <div
            className="p-5 font-cormorant"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            No hay eventos recientes para mostrar.
          </div>
        )}
        {audit.map((event) => (
          <div
            key={event.id}
            className="p-4 border-b last:border-0"
            style={{ borderColor: "var(--kd9-border)" }}
          >
            <div className="font-mont text-sm">{event.action}</div>
            <div
              className="font-mont text-[10px]"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              {event.actor_name ||
                event.actor_email ||
                event.actor_id ||
                "sistema"}{" "}
              · {event.target || "sin objetivo"} ·{" "}
              {event.at ? new Date(event.at).toLocaleString("es-CO") : ""}
            </div>
          </div>
        ))}
      </section>
    </PageShell>
  );
};

export default DirectorDashboard;
